import { Clock, DateTimeFormatter, Duration, LocalTime, ZonedDateTime, ZoneId } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';

export const SYSTEM_CLOCK = Clock.system(ZoneId.systemDefault());

export const getDateTimeFormatter = (pattern: string) =>
  DateTimeFormatter.ofPattern(pattern).withLocale(Locale.ENGLISH);

/**
 * Converts a ZonedDateTime to the user's local zone (system default) and formats it.
 * Use this when displaying timezone-aware timestamps so the user sees their local time.
 */
export const formatZonedDateTimeInLocalZone = (
  zonedDateTime: ZonedDateTime,
  pattern: string,
  clock: Clock = SYSTEM_CLOCK,
): string => zonedDateTime.withZoneSameInstant(clock.zone()).toLocalDateTime().format(getDateTimeFormatter(pattern));

/**
 * Formats a duration to amount of hours with 2 decimals or to "-" if 0.
 */
export const formatDurationToHours = (duration: Duration | undefined): string => {
  if (!duration) {
    return '-';
  }
  const hours = Math.round((duration.toMinutes() * 100) / 60) / 100;
  if (hours === 0) {
    return '-';
  }
  return hours.toString();
};

/**
 * Parses a decimal hours string to a Duration object
 * This is the inverse of formatDurationToHours
 */
export const parseHoursToDuration = (hoursString: string): Duration => {
  if (hoursString === '-' || !hoursString) {
    return Duration.ZERO;
  }

  const hours = parseFloat(hoursString);

  if (isNaN(hours) || hours === 0) {
    return Duration.ZERO;
  }

  return Duration.ofMinutes(Math.round(hours * 60));
};

/**
 * Parses an ISO offset date-time string and returns the corresponding js-joda LocalTime object.
 *
 * @param dateString - The ISO offset date-time string to parse.
 * @returns The LocalTime object representing the time portion of the input date-time string.
 *          Returns undefined if the input dateString is undefined.
 */
export const parseIsoOffsetZonedDateTimeToLocalTime = (dateTime: ZonedDateTime | undefined): LocalTime | undefined => {
  if (!dateTime) {
    return undefined;
  }
  return dateTime.toLocalTime();
};

/**
 * Gets the singular or plural form of "hour" based on the count.
 *
 * @param {number} hours - The number of hours.
 * @param {boolean} short - Whether to return the short form of the word.
 * @returns {string} "hour"/"hr" for hours equal to 1, "hours"/"hrs" for hours not equal to 1.
 */
export const getHourWord = (hours: number, short: boolean = false): string => {
  if (hours === 1) {
    return short ? 'hr' : 'hour';
  }

  return short ? 'hrs' : 'hours';
};
